import type { ReactNode } from 'react';

import { LOGO } from '@/lib/public-content';

/*
 * The frame every auth screen sits in.
 *
 * The brand name is `--bark-700`, and that is the whole point of this file
 * existing rather than each screen drawing its own header. **P3 is the
 * prototype's login page rendering "Honeycomb Cleaning Services" in `#FFDD33`
 * on white — 1.34:1 against a 3:1 requirement.** The brand's own name, on the
 * brand's own login page, invisible.
 *
 * ---
 *
 * **Two things changed on 2026-08-07, both spotted by the owner on the live
 * site.**
 *
 * It used to render its own full-page `<main>` with no header on it, so
 * `/login` was a dead end — nothing to click to get back to the site. The four
 * auth routes now sit inside `PublicLayout` (see App.tsx), so the real header,
 * the nav and the footer are all there, which is what every other site does.
 * This file is therefore a card in a page rather than a page.
 *
 * And the mark above the card was a **hand-drawn SVG hexagon** somebody built
 * out of the brand colours — not the business's actual logo, which has been
 * sitting in `assets/` since phase 8 and is what the public header already
 * uses. Same image, one source, so they cannot drift apart again.
 */

export function AuthCard({
  title,
  description,
  children,
  footer,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    /*
     * `min-h` rather than `min-h-screen`: the header and footer are real
     * elements above and below this now, so a full viewport height here would
     * push the footer off the bottom on every auth screen.
     */
    <div className="flex flex-col items-center justify-center bg-ink-50 px-4 py-12 sm:py-16">
      <div className="w-full max-w-[420px]">
        <div className="mb-8 flex flex-col items-center text-center">
          {/* The real logo. `LOGO` is the same record the public header reads. */}
          <img src={LOGO.src} alt={LOGO.alt} width={64} height={64} className="h-16 w-16" />
          {/* bark-700 on ink-50: 7.4:1. Never honey-400, which is 1.29:1. */}
          <p className="mt-3 text-h3 text-bark-700">Honeycomb Cleaning Services</p>
        </div>

        <div className="rounded-lg border border-ink-300 bg-white p-6 shadow-card sm:p-8">
          <h1 className="text-h2 text-ink-900">{title}</h1>
          {description ? <p className="mt-1.5 text-body text-ink-500">{description}</p> : null}
          <div className="mt-6">{children}</div>
        </div>

        {footer ? <div className="mt-6 text-center">{footer}</div> : null}
      </div>
    </div>
  );
}
